import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  ArrowLeft, 
  BookOpen, 
  Palette, 
  Trophy, 
  Brain,
  TestTube,
  Calculator,
  Globe,
  Music,
  Camera,
  GamepadIcon,
  Heart,
  Zap,
  Atom,
  Code,
  Leaf,
  Mountain,
  Car,
  Plane,
  Ship
} from 'lucide-react';
import useAutoScroll from '../../hooks/useAutoScroll';

// Types
interface Category {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  subcategories: Subcategory[];
}

interface Subcategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  type: 'academic' | 'non-academic';
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: white;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const Title = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  color: #ffd700;
`;

const Description = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.5;
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const CategoryCard = styled.div<{ isSelected: boolean }>`
  background: ${props => props.isSelected 
    ? 'rgba(255, 255, 255, 0.2)' 
    : 'rgba(255, 255, 255, 0.05)'
  };
  border: 2px solid ${props => props.isSelected 
    ? 'rgba(255, 255, 255, 0.4)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  border-radius: 20px;
  padding: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.isSelected ? props.color : 'transparent'};
    transition: all 0.3s ease;
  }
`;

const CategoryIcon = styled.div<{ color: string }>`
  font-size: 3rem;
  margin-bottom: 15px;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CategoryName = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 10px;
  text-align: center;
`;

const CategoryDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  line-height: 1.4;
  margin-bottom: 15px;
`;

const SubcategoryCount = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  display: inline-block;
  margin: 0 auto;
`;

const SubcategoriesSection = styled.div<{ isVisible: boolean }>`
  max-height: ${props => props.isVisible ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.5s ease;
  margin-top: 20px;
`;

const SubcategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 20px;
`;

const SubcategoryCard = styled.div<{ isSelected: boolean; type: 'academic' | 'non-academic' }>`
  background: ${props => props.isSelected 
    ? 'rgba(255, 255, 255, 0.2)' 
    : 'rgba(255, 255, 255, 0.05)'
  };
  border: 1px solid ${props => props.isSelected 
    ? 'rgba(255, 255, 255, 0.4)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  border-radius: 15px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    transform: translateY(-3px);
    background: rgba(255, 255, 255, 0.15);
  }
  
  border-left: 4px solid ${props => props.type === 'academic' ? '#4caf50' : '#ff9800'};
`;

const SubcategoryIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 10px;
  color: #ffd700;
`;

const SubcategoryName = styled.div`
  font-weight: 600;
  margin-bottom: 5px;
`;

const SubcategoryType = styled.div<{ type: 'academic' | 'non-academic' }>`
  font-size: 0.7rem;
  color: ${props => props.type === 'academic' ? '#4caf50' : '#ff9800'};
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 1px;
`;

const ContinueButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'linear-gradient(45deg, #4caf50, #8bc34a)'
  };
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  align-self: center;
  margin-top: 20px;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 10px 25px rgba(76, 175, 80, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
  }
`;

const CategorySelection: React.FC<{
  onSelect: (category: string, subcategory: string) => void;
  onBack: () => void;
}> = ({ onSelect, onBack }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const { scrollToElement, scrollToContinueButton } = useAutoScroll();

  const categories: Category[] = [
    {
      id: 'science',
      name: 'SCIENCE & TECHNOLOGY',
      icon: <TestTube />,
      color: '#4caf50',
      subcategories: [
        { id: 'biology', name: 'Biology', icon: <Leaf />, type: 'academic' },
        { id: 'chemistry', name: 'Chemistry', icon: <TestTube />, type: 'academic' },
        { id: 'physics', name: 'Physics', icon: <Zap />, type: 'academic' },
        { id: 'mathematics', name: 'Mathematics', icon: <Calculator />, type: 'academic' },
        { id: 'computer-science', name: 'Computer Science', icon: <Code />, type: 'academic' },
        { id: 'engineering', name: 'Engineering', icon: <Atom />, type: 'academic' }
      ]
    },
    {
      id: 'humanities',
      name: 'HUMANITIES & ARTS',
      icon: <Palette />,
      color: '#ff9800',
      subcategories: [
        { id: 'literature', name: 'Literature', icon: <BookOpen />, type: 'academic' },
        { id: 'history', name: 'History', icon: <Globe />, type: 'academic' },
        { id: 'philosophy', name: 'Philosophy', icon: <Brain />, type: 'academic' },
        { id: 'music', name: 'Music', icon: <Music />, type: 'non-academic' },
        { id: 'art', name: 'Visual Arts', icon: <Palette />, type: 'non-academic' },
        { id: 'photography', name: 'Photography', icon: <Camera />, type: 'non-academic' }
      ]
    },
    {
      id: 'sports',
      name: 'SPORTS & RECREATION',
      icon: <Trophy />,
      color: '#e91e63',
      subcategories: [
        { id: 'football', name: 'Football', icon: <Trophy />, type: 'non-academic' },
        { id: 'basketball', name: 'Basketball', icon: <Trophy />, type: 'non-academic' },
        { id: 'athletics', name: 'Athletics', icon: <Zap />, type: 'non-academic' },
        { id: 'swimming', name: 'Swimming', icon: <Ship />, type: 'non-academic' },
        { id: 'gaming', name: 'Gaming', icon: <GamepadIcon />, type: 'non-academic' },
        { id: 'fitness', name: 'Fitness', icon: <Heart />, type: 'non-academic' }
      ]
    },
    {
      id: 'general',
      name: 'GENERAL KNOWLEDGE',
      icon: <Brain />,
      color: '#9c27b0',
      subcategories: [
        { id: 'geography', name: 'Geography', icon: <Mountain />, type: 'academic' },
        { id: 'current-events', name: 'Current Events', icon: <Globe />, type: 'academic' },
        { id: 'entertainment', name: 'Entertainment', icon: <Music />, type: 'non-academic' },
        { id: 'automotive', name: 'Automotive', icon: <Car />, type: 'non-academic' },
        { id: 'aviation', name: 'Aviation', icon: <Plane />, type: 'non-academic' },
        { id: 'maritime', name: 'Maritime', icon: <Ship />, type: 'non-academic' }
      ]
    }
  ];

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory('');
    
    // Auto-scroll to subcategories section after a brief delay
    setTimeout(() => {
      scrollToElement('subcategories-section', { 
        behavior: 'smooth', 
        block: 'start',
        offset: 20 
      });
    }, 300);
  };

  const handleSubcategorySelect = (subcategoryId: string) => {
    setSelectedSubcategory(subcategoryId);
    
    // Auto-scroll to continue button after subcategory selection
    setTimeout(() => {
      scrollToContinueButton({ 
        behavior: 'smooth', 
        block: 'center',
        offset: 50 
      });
    }, 200);
  };

  const handleContinue = () => {
    if (selectedCategory && selectedSubcategory) {
      const category = categories.find(c => c.id === selectedCategory);
      const subcategory = category?.subcategories.find(s => s.id === selectedSubcategory);
      if (category && subcategory) {
        onSelect(category.name, subcategory.name);
      }
    }
  };

  const selectedCategoryData = categories.find(c => c.id === selectedCategory);

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>
          <ArrowLeft size={24} />
        </BackButton>
        <div>
          <Title>Choose Your Category</Title>
          <Description>
            Select a category and subcategory for your competition. 
            Academic categories focus on educational content, while non-academic categories 
            cover entertainment and recreational topics.
          </Description>
        </div>
      </Header>

      <CategoriesGrid>
        {categories.map((category) => (
          <CategoryCard
            key={category.id}
            isSelected={selectedCategory === category.id}
            onClick={() => handleCategorySelect(category.id)}
          >
            <CategoryIcon color={category.color}>
              {category.icon}
            </CategoryIcon>
            <CategoryName>{category.name}</CategoryName>
            <CategoryDescription>
              Explore {category.subcategories.length} subcategories covering both 
              academic and non-academic topics in this field.
            </CategoryDescription>
            <SubcategoryCount>
              {category.subcategories.length} subcategories
            </SubcategoryCount>
          </CategoryCard>
        ))}
      </CategoriesGrid>

      {selectedCategoryData && (
        <SubcategoriesSection id="subcategories-section" isVisible={!!selectedCategory}>
          <h3 style={{ 
            fontSize: '1.4rem', 
            fontWeight: 600, 
            marginBottom: '20px',
            color: '#ffd700',
            textAlign: 'center'
          }}>
            Select a Subcategory
          </h3>
          
          <SubcategoriesGrid>
            {selectedCategoryData.subcategories.map((subcategory) => (
              <SubcategoryCard
                key={subcategory.id}
                isSelected={selectedSubcategory === subcategory.id}
                type={subcategory.type}
                onClick={() => handleSubcategorySelect(subcategory.id)}
              >
                <SubcategoryIcon>
                  {subcategory.icon}
                </SubcategoryIcon>
                <SubcategoryName>{subcategory.name}</SubcategoryName>
                <SubcategoryType type={subcategory.type}>
                  {subcategory.type}
                </SubcategoryType>
              </SubcategoryCard>
            ))}
          </SubcategoriesGrid>
        </SubcategoriesSection>
      )}

      <ContinueButton
        id="continue-to-mode-button"
        disabled={!selectedCategory || !selectedSubcategory}
        onClick={handleContinue}
        className="continue-button"
      >
        Continue to Mode Selection
      </ContinueButton>
    </Container>
  );
};

export default CategorySelection;
